import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { LoadingService, ModalLoadingContext, ToastService } from '@siemens/ix-angular';
import { Router } from '@angular/router';
import { catchError, defer, finalize, from, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { saveAs } from '../utils/file-download.util';
import { ExcelField } from 'src/const-data/form-field-mappings';
import { ExcelReadMap, ExcelWriteMap } from './form-data.service';

@Injectable({
  providedIn: 'root'
})
export class DataTransferService {
  constructor(
    private readonly router: Router,
    private readonly httpClient: HttpClient,
    private readonly toastService: ToastService,
    private readonly loadingService: LoadingService
  ) {}

  private navigateToProjectsParent(): void {
    const currentUrl = this.router.url;
    const projectSegmentIndex = currentUrl.indexOf('projects/');

    if (projectSegmentIndex === -1) {
      this.router.navigateByUrl(this.router.parseUrl('/'));
      return;
    }

    const parentUrl = currentUrl.substring(0, projectSegmentIndex);
    this.router.navigateByUrl(this.router.parseUrl(parentUrl || '/'));
  }

  private handleEmptyProjectId(): Observable<never> {
    this.toastService.show({
      title: 'No project selected',
      message: 'You need to open a project first.',
      type: 'error'
    });
    this.navigateToProjectsParent();
    return throwError(() => new Error('No project selected'));
  }

  public writeDataToExcel(dataToWrite: ExcelWriteMap, projectId: string): Observable<void> {
    return defer(() => {
      const normalizedProjectId = projectId.trim();
      if (normalizedProjectId === '') {
        return this.handleEmptyProjectId();
      }

      if (Object.keys(dataToWrite).length === 0) {
        return of(void 0);
      }

      const body = dataToWrite;
      const options = {
        observe: 'response' as const,
        responseType: 'text' as const
      };
      const url = environment.baseUrl + `/calculation-file/write/${normalizedProjectId}`;

      return from(this.loadingService.showModalLoading({ message: 'Saving data..' })).pipe(
        switchMap((progress: ModalLoadingContext) =>
          this.httpClient.post(url, body, options).pipe(
            map(() => void 0),
            catchError(error => {
              const details = error.message || '';
              this.toastService.show({
                title: 'Update error',
                message: 'We cannot update the values of your calculation. ' + details,
                type: 'error'
              });
              return throwError(() => error);
            }),
            finalize(() => {
              progress.finish();
            })
          )
        )
      );
    });
  }

  public getValuesFromExcel(keys: ExcelField[], projectId: string): Observable<ExcelReadMap> {
    return defer(() => {
      const normalizedProjectId = projectId.trim();
      if (normalizedProjectId === '') {
        return this.handleEmptyProjectId();
      }

      const url = environment.baseUrl + `/calculation-file/read/${normalizedProjectId}`;
      const body = keys;
      const options = {
        observe: 'response' as const
      };

      return from(this.loadingService.showModalLoading({ message: 'Loading data..' })).pipe(
        switchMap((progress: ModalLoadingContext) =>
          this.httpClient.post(url, body, options).pipe(
            map(response => response.body as ExcelReadMap),
            catchError(error => {
              const details = error.message || '';
              this.toastService.show({
                title: 'Load data error',
                message: 'We cannot load the data. ' + details,
                type: 'error'
              });
              this.navigateToProjectsParent();

              return throwError(() => error);
            }),
            finalize(() => {
              progress.finish();
            })
          )
        )
      );
    });
  }

  public getProjectFile(id: string): Observable<void> {
    const url = environment.baseUrl + `/calculation-file/download/${id}`;
    const options = {
      responseType: 'blob' as const
    };

    return this.httpClient.get(url, options).pipe(
      tap((response: Blob) => {
        saveAs(response, `${id}.xlsx`);
      }),
      map(() => void 0),
      catchError(error => {
        const details = error.message || '';
        this.toastService.show({
          title: 'Download error',
          message: 'We cannot download the file from the server. Try again. ' + details,
          type: 'error'
        });
        return throwError(() => error);
      })
    );
  }
}
